import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import clsx from 'clsx'
import { useForm } from 'react-hook-form'
import Icon from '../../../../components/atoms/Icon'
import { ICON_NAMES } from '../../../../constants'
import SydButton from '../../../../components/commonDesign/Button'
import Loading from '../../../../components/molecules/Loading'
import { actionPipelines } from '../../../../api/coreData'
import { useSubmitDenaliAction } from '../../../../api/denaliAssistant'
import FieldGroup from '../../../../components/organisms/DenaliActionPipeline/form/FieldGroup'
import usePipelineQuery from '../../../../components/organisms/DenaliDataPipeline/hooks/usePipelineQuery'
import {
  buildDefaultValues,
  normalizeFormDefaults,
  sortByOrdinal,
  coerceFormValues,
  getAllFields,
  buildArgsFromMapping
} from '../../../../components/organisms/DenaliActionPipeline/form/utils/fieldUtils'
import QuestionsRenderer from './QuestionsRenderer'
import QuestionnaireAnswersSummary from './QuestionnaireAnswersSummary'

const useStyles = makeStyles((theme) => ({
  actions: {
    fontSize: 'var(--font-size-md)',
    color: 'var(--text-color-main)',
    fontWeight: 'var(--font-weight-light)',
    lineHeight: 1.6,
    margin: '12px 0'
  },
  actionLink: {
    color: 'var(--color-primary-500)',
    cursor: 'pointer',
    textDecoration: 'underline',
    '&:hover': {
      color: 'var(--color-primary-600)'
    }
  },
  threadLink: {
    color: 'var(--color-primary-500)',
    cursor: 'pointer',
    textDecoration: 'underline',
    display: 'inline-flex',
    alignItems: 'center',
    '&:hover': {
      color: 'var(--color-primary-600)'
    }
  },
  actionDisabled: {
    color: 'var(--color-neutral-400)',
    textDecoration: 'line-through',
    cursor: 'not-allowed'
  },
  errorMessage: {
    fontSize: 'var(--font-size-md)',
    color: 'var(--color-neutral-500)',
    fontStyle: 'italic',
    margin: '12px 0'
  },
  formContainer: {
    margin: '16px 0',
    padding: '20px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-md)',
    backgroundColor: 'var(--dai-color-surface4)',
    fontFamily: 'var(--font-sans)',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    boxSizing: 'border-box',
    '& .MuiGrid-container': {
      display: 'flex',
      flexWrap: 'wrap'
    },
    '& .MuiGrid-item': {
      overflow: 'hidden'
    },
    '& .MuiGrid-grid-xs-6': {
      flexBasis: '50%',
      maxWidth: '50%',
      flexGrow: 0
    },
    '& .MuiGrid-grid-xs-12': {
      flexBasis: '100%',
      maxWidth: '100%',
      flexGrow: 0
    },
    [theme.breakpoints.up('sm')]: {
      '& .MuiGrid-grid-sm-6': {
        flexBasis: '50%',
        maxWidth: '50%',
        flexGrow: 0
      },
      '& .MuiGrid-grid-sm-12': {
        flexBasis: '100%',
        maxWidth: '100%',
        flexGrow: 0
      }
    }
  },
  formTitle: {
    fontSize: 'var(--font-size-lg)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--dai-color-text)',
    marginBottom: '16px',
    fontFamily: 'var(--font-sans)',
    lineHeight: 1.4
  },
  formActions: {
    display: 'flex',
    gap: '12px',
    marginTop: '24px',
    justifyContent: 'flex-end'
  },
  formError: {
    color: 'var(--color-error)',
    fontSize: 'var(--font-size-sm)',
    marginBottom: '12px'
  },
  formSuccessContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 0'
  },
  formSuccessMessage: {
    fontSize: 'var(--font-size-md)',
    color: 'var(--dai-color-text)',
    fontFamily: 'var(--font-sans)'
  },
  formLoadingOverlay: {
    position: 'relative',
    opacity: 0.6,
    pointerEvents: 'none'
  },
  formLoadingIndicator: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 10
  },
  formCollapsed: {
    padding: '16px 20px',
    transition: 'height 0.3s ease-out'
  },
  workflowEventCard: {
    margin: '16px 0',
    padding: '16px 20px',
    border: '1px solid var(--color-primary-200)',
    borderRadius: 'var(--border-radius-md)',
    backgroundColor: 'var(--color-primary-050, rgba(59, 130, 246, 0.04))',
    fontFamily: 'var(--font-sans)',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px'
  },
  workflowEventIcon: {
    color: 'var(--color-primary-500)',
    flexShrink: 0,
    marginTop: '2px'
  },
  workflowEventContent: {
    flex: 1,
    minWidth: 0
  },
  workflowEventLabel: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-primary-700)',
    marginBottom: '4px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  workflowEventMessage: {
    fontSize: 'var(--font-size-md)',
    color: 'var(--dai-color-text)',
    lineHeight: 1.5
  }
}))

class ActionType {
  constructor (data) {
    this.data = data
  }

  getLabel () {
    return this.data.label || `[${this.data.type}]`
  }

  isValid () {
    return true
  }

  render () {
    return null
  }
}

class PromptAction extends ActionType {
  isValid () {
    return !!(this.data.label && this.data.prompt)
  }

  render (classes, onSuggestionClick) {
    const opts = this.data.skillId ? { skillId: this.data.skillId } : undefined
    return (
      <span
        className={classes.actionLink}
        onClick={() => onSuggestionClick?.(this.data.prompt, opts)}
      >
        {this.data.label}
      </span>
    )
  }
}

class ThreadAction extends ActionType {
  isValid () {
    return !!(this.data.label && this.data.prompt)
  }

  render (classes) {
    const handleClick = () => {
      const params = new URLSearchParams()
      params.set('prompt', this.data.prompt)
      params.set('focusType', this.data.focus?.type || '')
      params.set('focusSubjectIdentifier', this.data.focus?.subjectIdentifier || '')
      params.set('focusSubjectLabel', this.data.focus?.subjectLabel || '')
      window.open(`/denali-ai/new?${params.toString()}`, '_blank')
    }

    return (
      <span
        className={classes.threadLink}
        onClick={handleClick}
      >
        <span>{this.data.label}</span>
        <Icon name={ICON_NAMES.openInNew} customSize='14px' style={{ marginLeft: '4px', opacity: 0.7 }} />
      </span>
    )
  }
}

class FormAction extends ActionType {
  isValid () {
    return !!(this.data.label && this.data.code && this.data.actionTraceId)
  }

  render (classes, message) {
    return <ActionTemplateRenderer data={this.data} classes={classes} message={message} />
  }
}

class QuestionsAction extends ActionType {
  isValid () {
    return !!(this.data.label && this.data.actionTraceId && Array.isArray(this.data.questions) && this.data.questions.length > 0)
  }

  render (classes, message) {
    return <QuestionsRenderer data={this.data} message={message} />
  }
}

class QuestionnaireAnswersAction extends ActionType {
  isValid () {
    return !!(this.data.label && Array.isArray(this.data.questions) && this.data.questions.length > 0)
  }

  render () {
    return <QuestionnaireAnswersSummary data={this.data} />
  }
}

class WorkflowEventAction extends ActionType {
  isValid () {
    return !!(this.data.label && this.data.message)
  }

  render (classes) {
    return (
      <div className={classes.workflowEventCard}>
        <Icon
          name={ICON_NAMES.bolt}
          customSize='20px'
          additionalClasses={classes.workflowEventIcon}
        />
        <div className={classes.workflowEventContent}>
          <div className={classes.workflowEventLabel}>{this.data.label}</div>
          <div className={classes.workflowEventMessage}>{this.data.message}</div>
        </div>
      </div>
    )
  }
}

class UnsupportedAction extends ActionType {
  render (classes, onSuggestionClick) {
    return (
      <span className={classes.actionDisabled}>
        {this.getLabel()}
      </span>
    )
  }
}

const ACTION_TYPE_REGISTRY = {
  prompt: PromptAction,
  thread: ThreadAction,
  form: FormAction,
  questions: QuestionsAction,
  'questionnaire-answers': QuestionnaireAnswersAction,
  'workflow-event': WorkflowEventAction
}

function createAction (data) {
  if (!data || typeof data !== 'object' || !data.type) {
    return null
  }

  const ActionClass = ACTION_TYPE_REGISTRY[data.type]
  if (ActionClass) {
    const action = new ActionClass(data)
    return action.isValid() ? action : null
  }

  return new UnsupportedAction(data)
}

function ActionTemplateRenderer ({ data, classes, message }) {
  const submitAction = useSubmitDenaliAction()
  const { data: templateData, isLoading, error: loadError } = actionPipelines.useActionTemplate(data.code)
  const [submitError, setSubmitError] = useState(null)

  const actionState = message?.data?.actions?.[data.actionTraceId]
  const isComplete = actionState?.actionStatus === 'complete'
  const isError = actionState?.actionStatus === 'error'
  const isCorrectableError = actionState?.actionStatus === 'correctable_error'

  const actionTemplate = templateData

  const { groups, dataPipelineCode, argMapping } = useMemo(() => ({
    groups: actionTemplate?.definition?.fieldMapping?.groups || [],
    dataPipelineCode: actionTemplate?.definition?.dataPipelineCode,
    argMapping: actionTemplate?.definition?.argMapping
  }), [actionTemplate])

  const initDisabled = !dataPipelineCode
  const { data: initResult, isLoading: initLoading } = usePipelineQuery(
    dataPipelineCode,
    data.defaults || {},
    initDisabled
  )

  const normalizedGroups = useMemo(() => {
    return groups.map(group => {
      if (group.fields && !Array.isArray(group.fields)) {
        return { ...group, fields: Object.values(group.fields) }
      }
      return group
    })
  }, [groups])

  const defaultValues = useMemo(() => {
    if (!initDisabled && initLoading) return {}
    const defaults = data.defaults || {}
    const initData = { ...defaults, ...(initResult || {}) }
    const templateDefaults = buildDefaultValues(normalizedGroups, initData)
    const merged = { ...templateDefaults, ...defaults }
    return normalizeFormDefaults(merged, normalizedGroups)
  }, [normalizedGroups, data.defaults, initDisabled, initLoading, initResult])

  const form = useForm({
    mode: 'onChange',
    reValidateMode: 'onSubmit',
    criteriaMode: 'all',
    defaultValues
  })

  // Apply initial defaults once async init data is ready, then never again;
  // form.reset() on subsequent defaultValues churn would clobber user input.
  const hasAppliedInitialDefaults = useRef(false)
  const initReady = initDisabled || !initLoading
  useEffect(() => {
    if (hasAppliedInitialDefaults.current) return
    if (!initReady) return
    if (Object.keys(defaultValues).length === 0) return
    form.reset(defaultValues)
    hasAppliedInitialDefaults.current = true
  }, [defaultValues, form, initReady])

  useEffect(() => {
    if (isCorrectableError) {
      setSubmitError(actionState?.error || 'Please correct the form and try again.')
    }
  }, [isCorrectableError, actionState?.error])

  const sortedGroups = useMemo(() => sortByOrdinal(normalizedGroups), [normalizedGroups])
  const allFields = useMemo(() => getAllFields(normalizedGroups), [normalizedGroups])

  const handleSubmit = useCallback(async (formData) => {
    setSubmitError(null)

    try {
      const coercedData = coerceFormValues(formData, allFields, normalizedGroups)
      const args = buildArgsFromMapping(coercedData, argMapping)

      await submitAction.mutateAsync({
        actionTraceId: data.actionTraceId,
        input: {
          code: actionTemplate?.definition?.actionPipelineCode,
          args
        },
        message
      })
    } catch (error) {
      setSubmitError(error?.message || error?.toString() || 'Failed to submit. Please try again.')
    }
  }, [allFields, normalizedGroups, argMapping, data.actionTraceId, actionTemplate, submitAction, message])

  const submitting = submitAction.isLoading

  if (isComplete) {
    return (
      <div className={clsx(classes.formContainer, classes.formCollapsed)}>
        <div className={classes.formSuccessContainer}>
          <Icon
            name={ICON_NAMES.checkCircle}
            customSize='20px'
            style={{ color: 'var(--color-success)' }}
          />
          <div className={classes.formSuccessMessage}>
            {actionState?.message || `${data.label} completed successfully`}
          </div>
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className={clsx(classes.formContainer, classes.formCollapsed)}>
        <div className={classes.formError}>
          {actionState?.error || `${data.label} failed`}
        </div>
      </div>
    )
  }

  if (isLoading || (!initDisabled && initLoading)) {
    return (
      <div className={classes.formContainer}>
        <Loading />
      </div>
    )
  }

  if (loadError) {
    return (
      <div className={classes.formContainer}>
        <div className={classes.formError}>
          Failed to load action template: {loadError.message || 'Unknown error'}
        </div>
      </div>
    )
  }

  return (
    <form className={classes.formContainer} onSubmit={form.handleSubmit(handleSubmit)}>
      <div className={classes.formTitle}>{actionTemplate?.name || data.label}</div>

      {submitError && (
        <div className={classes.formError}>{submitError}</div>
      )}

      <div className={submitting ? classes.formLoadingOverlay : undefined}>
        {submitting && (
          <div className={classes.formLoadingIndicator}>
            <Loading />
          </div>
        )}

        {sortedGroups.map((group, index) => (
          <FieldGroup
            key={group.id || `group-${index}`}
            group={group}
            control={form.control}
            form={form}
            initializationData={initResult || data.defaults}
          />
        ))}

        <div className={classes.formActions}>
          <SydButton
            variant='ghost'
            type='button'
            onClick={() => {
              form.reset(defaultValues)
              setSubmitError(null)
            }}
            disabled={submitting}
          >
            Reset
          </SydButton>
          <SydButton
            variant='primary'
            type='submit'
            processing={submitting}
            disabled={!form.formState.isValid || submitting}
          >
            Submit
          </SydButton>
        </div>
      </div>
    </form>
  )
}

ActionTemplateRenderer.propTypes = {
  data: PropTypes.shape({
    label: PropTypes.string.isRequired,
    code: PropTypes.string.isRequired,
    actionTraceId: PropTypes.string.isRequired,
    defaults: PropTypes.object
  }).isRequired,
  classes: PropTypes.object.isRequired,
  message: PropTypes.object
}

function InlineActions ({ content, onSuggestionClick, message }) {
  const classes = useStyles()

  const { actions, error } = useMemo(() => {
    try {
      const text = content || ''
      const firstBracket = text.indexOf('[')
      const lastBracket = text.lastIndexOf(']')

      if (firstBracket === -1 || lastBracket === -1 || firstBracket >= lastBracket) {
        return { error: 'Invalid JSON array format', actions: [] }
      }

      const jsonContent = text.substring(firstBracket, lastBracket + 1)
      const parsed = JSON.parse(jsonContent)

      if (!Array.isArray(parsed)) {
        return { error: 'Actions must be a JSON array', actions: [] }
      }

      const actions = []

      for (const item of parsed) {
        const action = createAction(item)
        if (action) {
          actions.push(action)
        }
      }

      return { actions }
    } catch (err) {
      console.error(err)
      return { error: 'Failed to parse actions JSON', actions: [] }
    }
  }, [content])

  if (error) {
    return (
      <div className={classes.errorMessage}>
        I tried to suggest follow up actions, but something went wrong.
      </div>
    )
  }

  if (actions.length === 0) {
    return null
  }

  if (actions.length === 1 && actions[0] instanceof FormAction) {
    return actions[0].render(classes, message)
  }

  if (actions.length === 1 && actions[0] instanceof QuestionsAction) {
    return actions[0].render(classes, message)
  }

  if (actions.length === 1 && actions[0] instanceof QuestionnaireAnswersAction) {
    return actions[0].render()
  }

  if (actions.length === 1 && actions[0] instanceof WorkflowEventAction) {
    return actions[0].render(classes)
  }

  const formatText = () => {
    if (actions.length === 1) {
      const action = actions[0]
      const renderProps = action instanceof ThreadAction ? undefined : onSuggestionClick
      return (
        <>
          Would you like to {action.render(classes, renderProps)}?
        </>
      )
    }

    const renderedActions = actions.map((action, index) => {
      const renderProps = action instanceof ThreadAction ? undefined : onSuggestionClick
      return (
        <React.Fragment key={index}>
          {action.render(classes, renderProps)}
          {index < actions.length - 2 && ', '}
          {index === actions.length - 2 && ', or '}
        </React.Fragment>
      )
    })

    return <>Would you like to {renderedActions}?</>
  }

  return (
    <div className={classes.actions}>
      {formatText()}
    </div>
  )
}

InlineActions.propTypes = {
  content: PropTypes.string.isRequired,
  onSuggestionClick: PropTypes.func,
  message: PropTypes.object
}

export default InlineActions
